import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Check, Gift, ShoppingBag } from "lucide-react";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import FrequentlyBoughtTogether from "@/components/FrequentlyBoughtTogether";

const formatPrice = (n) => `$${Number(n).toFixed(2)}`;
export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    setActiveImage(0);
    setSelectedAddons([]);
    fetchProduct(slug)
      .then(setProduct)
      .catch(() => setProduct(null));
    fetchProducts()
      .then((all) =>
        setRelated(all.filter((p) => p.slug !== slug).slice(0, 3))
      )
      .catch(() => setRelated([]));
  }, [slug]);

  const addons = product?.addons || [];
  const hasAddons = addons.length > 0;

  const total = useMemo(() => {
    if (!product) return 0;
    const base = Number(product.price || 0);
    const extra = addons
      .filter((a) => selectedAddons.includes(a.id))
      .reduce((sum, a) => sum + Number(a.price || 0), 0);
    return base + extra;
  }, [product, addons, selectedAddons]);

  const toggleAddon = (id) => {
    setSelectedAddons((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24" data-testid="product-loading">
        <div className="aspect-[4/3] bg-[#F5F2EB] animate-pulse" />
      </div>
    );
  }

  const gallery = product.gallery?.length ? product.gallery : [product.image_url];

  return (
    <div data-testid="product-page" className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-24">
      <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-[#6F4E37] link-underline">
        <ArrowLeft size={14} /> Back to shop
      </Link>

      <div className="mt-10 grid md:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="md:col-span-7">
          <div className="aspect-[4/5] bg-[#F5F2EB] overflow-hidden">
            <img
              src={gallery[activeImage]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square bg-[#F5F2EB] overflow-hidden border ${
                    activeImage === i ? "border-[#2C2421]" : "border-transparent"
                  }`}
                >
                  <img src={g} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-5 md:sticky md:top-28 self-start">
          <div className="eyebrow mb-4">{(product.tags || []).join(" · ") || "Handcrafted"}</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            {product.name}
          </h1>
          <div className="mt-4 text-2xl font-serif text-[#6F4E37]" data-testid="product-base-price">
            {product.price_label}
          </div>

          <p className="mt-8 text-[#2C2421]/80 leading-relaxed">
            {product.description}
          </p>

          {product.includes?.length > 0 && (
            <div
              data-testid="includes-section"
              className="mt-8 bg-[#F5F2EB] border border-[#E5E0D8] p-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <Gift size={16} className="text-[#6F4E37]" />
                <div className="eyebrow !text-[#6F4E37] !text-[0.7rem]">Included with this board</div>
              </div>
              <ul className="space-y-2">
                {product.includes.map((item) => (
                  <li
                    key={item}
                    data-testid={`include-${item.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 text-sm text-[#2C2421]"
                  >
                    <Check size={14} className="text-[#4A5D4E] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-[#6F4E37] leading-relaxed">
                Everything you need to begin the care routine and keep your board in prime condition.
              </p>
            </div>
          )}

          {hasAddons && (
            <div className="mt-10" data-testid="addons-section">
              <div className="eyebrow mb-4">Add-ons</div>
              <ul className="border-t border-[#E5E0D8]">
                {addons.map((a) => {
                  const checked = selectedAddons.includes(a.id);
                  return (
                    <li key={a.id} className="border-b border-[#E5E0D8]">
                      <button
                        type="button"
                        data-testid={`addon-${a.id}`}
                        onClick={() => toggleAddon(a.id)}
                        className="w-full flex items-center justify-between gap-4 py-4 text-left group"
                        aria-pressed={checked}
                      >
                        <span className="flex items-center gap-3">
                          <span
                            className={`h-5 w-5 inline-flex items-center justify-center border transition-colors ${
                              checked
                                ? "bg-[#2C2421] border-[#2C2421] text-[#FDFBF7]"
                                : "border-[#A89F91] bg-transparent text-transparent group-hover:border-[#2C2421]"
                            }`}
                          >
                            <Check size={12} strokeWidth={3} />
                          </span>
                          <span className={`text-sm ${checked ? "text-[#2C2421]" : "text-[#2C2421]/85"}`}>
                            {a.label}
                          </span>
                        </span>
                        <span className="text-sm font-medium text-[#2C2421]">
                          +{formatPrice(a.price)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 flex items-baseline justify-between">
                <div className="eyebrow !text-[#A89F91]">Total</div>
                <div
                  data-testid="product-total"
                  className="font-serif text-3xl text-[#2C2421]"
                >
                  {formatPrice(total)}
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-col gap-3">
            <button
              data-testid="add-to-cart-btn"
              onClick={() => {
                addItem(product, selectedAddons);
                toast.success(`${product.name} added to cart`, {
                  action: { label: "View cart", onClick: () => navigate("/cart") },
                });
              }}
              className="inline-flex items-center justify-center gap-2 bg-[#2C2421] text-[#FDFBF7] py-4 px-6 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
            >
              <ShoppingBag size={14} /> Add to cart — {formatPrice(total)}
            </button>
            <Link
              to="/contact"
              data-testid="contact-link"
              className="inline-flex items-center justify-center gap-2 border border-[#2C2421] text-[#2C2421] py-4 px-6 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
            >
              Contact the workshop <ChevronRight size={14} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 text-sm">
            {product.dimensions && (
              <div className="border-t border-[#E5E0D8] pt-5" data-testid="meta-dimensions">
                <div className="eyebrow !text-[#A89F91] mb-1">Dimensions</div>
                <div>{product.dimensions}</div>
              </div>
            )}
            <div className="border-t border-[#E5E0D8] pt-5">
              <div className="eyebrow !text-[#A89F91] mb-1">Made</div>
              <div>By hand, in Whangamata</div>
            </div>
            <div className="border-t border-[#E5E0D8] pt-5">
              <div className="eyebrow !text-[#A89F91] mb-1">Lead time</div>
              <div>1–2 weeks for made-to-order</div>
            </div>
            <div className="border-t border-[#E5E0D8] pt-5">
              <div className="eyebrow !text-[#A89F91] mb-1">Finish</div>
              <div>Food-safe mineral oil and then coated with Board Butter</div>
            </div>
            <div className="border-t border-[#E5E0D8] pt-5">
              <div className="eyebrow !text-[#A89F91] mb-1">Care</div>
              <div>Refresh with Board Butter</div>
            </div>
          </div>
        </div>
      </div>

      <FrequentlyBoughtTogether mainProduct={product} />

      {related.length > 0 && (
        <section className="mt-32">
          <h3 className="font-serif text-3xl mb-10">Also in the workshop</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.slug}`}
                data-testid={`related-${p.slug}`}
                className="block group"
              >
                <div className="product-img-wrap aspect-[4/5] bg-[#F5F2EB] mb-4">
                  <img src={p.image_url} alt={p.name} className="product-img w-full h-full object-cover" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-serif text-lg">{p.name}</div>
                  <div className="text-sm">{p.price_label}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}